import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap"
import { InputSwitch } from "primereact/inputswitch"
import axios from "axios"
import Swal from "sweetalert2"

const DEFAULTS = {
  calling_email_enabled: false,
  calling_sms_enabled: false,
  calling_whatsapp_enabled: false,
}
const ACCESS_ERROR =
  "You need an active ATS account to edit calling notification settings."
const bool = value =>
  value === true || value === 1 || String(value).toLowerCase() === "true"

export default function CallingNotificationSettings() {
  useEffect(() => {
    const previousTitle = document.title
    document.title = "Calling Notification Settings | Bharat ATS"
    return () => {
      document.title = previousTitle
    }
  }, [])
  const [companies, setCompanies] = useState([])
  const [company, setCompany] = useState(null)
  const [settings, setSettings] = useState(DEFAULTS)
  const [original, setOriginal] = useState(DEFAULTS)
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: "", text: "" })
  useEffect(() => {
    if (!message.text) return
    Swal.fire({
      icon: message.type === "danger" ? "error" : "success",
      title: message.type === "danger" ? "Unable to Update Settings" : "Settings Updated",
      text: message.text,
      confirmButtonText: "OK",
    })
    setMessage({ type: "", text: "" })
  }, [message])
  const user = useMemo(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("user-info") || "{}")
      return stored.user_details || stored
    } catch (error) {
      return {}
    }
  }, [])
  const role = String(user.role_name || user.role || user.user_type || "")
    .toLowerCase()
    .replace(/[_-]/g, " ")
  // Any authenticated user with ATS access may manage the selected company's
  // notification settings. Company/role restrictions are enforced by the API.
  const canEdit = Boolean(
    user.has_ats_access === true ||
      user.has_ats_access === 1 ||
      String(user.has_ats_access).toLowerCase() === "true" ||
      user.ats_access === true ||
      user.is_authenticated === true ||
      user.user_id ||
      user.id
  )
  const companyId = company?.id || company?.company_id
  const isVgus =
    String(company?.company_domain || "").toLowerCase() === "vgus"
  const supportedChannelFields = isVgus
    ? ["calling_email_enabled", "calling_sms_enabled"]
    : ["calling_email_enabled", "calling_whatsapp_enabled"]
  const label = item => {
    const name = item?.company_name || item?.name
    const domain = item?.company_domain
    if (!name && !domain) return "--"
    if (!name) return domain
    if (!domain) return name
    return `${name} - ${domain}`
  }
  const renderLabel = item => {
    const name = item?.company_name || item?.name
    const domain = item?.company_domain
    if (!name && !domain) return "--"
    if (!name) return <strong>{domain}</strong>
    if (!domain) return name
    return <>{name} - <strong>{domain}</strong></>
  }
  const options = companies.filter(item =>
    label(item).toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    let active = true
    axios
      .get("/api/v1/company/?size=1000")
      .then(({ data }) => {
        if (!active) return
        const rows = Array.isArray(data)
          ? data
          : data.results || data.data || []
        setCompanies(
          rows.filter(
            item =>
              item.is_active !== false &&
              item.active !== false &&
              item.status !== "inactive"
          )
        )
      })
      .catch(
        error =>
          active &&
          setMessage({
            type: "danger",
            text:
              error.response?.status === 403
                ? ACCESS_ERROR
                : "Unable to load companies.",
          })
      )
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])
  useEffect(() => {
    if (!companyId) return undefined
    let active = true
    setLoadingSettings(true)
    setMessage({ type: "", text: "" })
    axios
      .get(`/api/v1/company/${companyId}/`)
      .then(({ data }) => {
        if (!active) return
        const loaded = {
          calling_email_enabled: bool(data.calling_email_enabled),
          calling_sms_enabled: isVgus && bool(data.calling_sms_enabled),
          calling_whatsapp_enabled: !isVgus && bool(data.calling_whatsapp_enabled),
        }
        setSettings(loaded)
        setOriginal(loaded)
      })
      .catch(
        error =>
          active &&
          setMessage({
            type: "danger",
            text:
              error.response?.status === 403
                ? ACCESS_ERROR
                : error.response?.status === 404
                ? "Company was not found."
                : "Unable to load calling notification settings.",
          })
      )
      .finally(() => active && setLoadingSettings(false))
    return () => {
      active = false
    }
  }, [companyId, isVgus])
  const update = (field, value) =>
    setSettings(current => ({ ...current, [field]: value }))
  const save = async () => {
    const payload = {}
    supportedChannelFields.forEach(field => {
      if (settings[field] !== original[field])
        payload[field] = Boolean(settings[field])
    })
    if (!Object.keys(payload).length) return
    setSaving(true)
    setMessage({ type: "", text: "" })
    try {
      await axios.patch(`/api/v1/company/${companyId}/`, payload)
      const { data } = await axios.get(`/api/v1/company/${companyId}/`)
      const loaded = {
        calling_email_enabled: bool(data.calling_email_enabled),
        calling_sms_enabled: isVgus && bool(data.calling_sms_enabled),
        calling_whatsapp_enabled: !isVgus && bool(data.calling_whatsapp_enabled),
      }
      setSettings(loaded)
      setOriginal(loaded)
      setMessage({
        type: "success",
        text: "Calling notification settings saved successfully.",
      })
    } catch (error) {
      setMessage({
        type: "danger",
        text:
          error.response?.status === 403
            ? ACCESS_ERROR
            : error.response?.status === 404
            ? "Company was not found."
            : error.response?.data?.detail ||
              error.response?.data?.message ||
              error.response?.data?.error ||
              (typeof error.response?.data === "string" ? error.response.data : null) ||
              "Unable to save calling notification settings.",
      })
    } finally {
      setSaving(false)
    }
  }

  if (!canEdit)
    return (
      <Container className="py-5">
        <Card>
          <CardBody>
            <h4>Access denied</h4>
            <p className="mb-0">{ACCESS_ERROR}</p>
          </CardBody>
        </Card>
      </Container>
    )
  return (
    <div className="page-content">
      <Container fluid>
        <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h1 className="page-title mb-1">Calling Notification Settings</h1>
            <p className="text-muted mb-0">
              Configure outreach notifications for the selected company.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="position-relative" style={{ width: 360 }}>
              <input
                className="form-control"
                style={{ paddingRight: search ? 38 : undefined }}
                value={search}
                onFocus={() => setOpen(true)}
                onChange={event => {
                  setSearch(event.target.value)
                  setOpen(true)
                }}
                placeholder={
                  loading ? "Loading companies..." : "Search companies..."
                }
                disabled={loading || saving}
              />
              {search && !loading && (
                <button
                  type="button"
                  aria-label="Clear company search"
                  title="Clear search"
                  onClick={() => {
                    setSearch("")
                    setCompany(null)
                    setOpen(false)
                    setSettings(DEFAULTS)
                    setOriginal(DEFAULTS)
                    setMessage({ type: "", text: "" })
                  }}
                  disabled={saving}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    transform: "translateY(-50%)",
                    width: 26,
                    height: 26,
                    padding: 0,
                    border: 0,
                    borderRadius: "50%",
                    background: "transparent",
                    color: "#64748b",
                    fontSize: 21,
                    lineHeight: 1,
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  ×
                </button>
              )}
              {open && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm w-100 mt-1"
                  style={{ zIndex: 30, maxHeight: 260, overflowY: "auto" }}
                >
                  {options.map(item => (
                    <button
                      type="button"
                      key={item.id || item.company_id}
                      className="dropdown-item text-start"
                      style={{
                        whiteSpace: "normal",
                        padding: "12px 14px",
                        minHeight: 46,
                        borderBottom: "1px solid #eef1f5",
                      }}
                      onClick={() => {
                        const nextIsVgus =
                          String(item?.company_domain || "").toLowerCase() === "vgus"
                        setCompany(item)
                        setSettings(current => ({
                          ...current,
                          calling_sms_enabled: nextIsVgus && current.calling_sms_enabled,
                          calling_whatsapp_enabled: !nextIsVgus && current.calling_whatsapp_enabled,
                        }))
                        setOriginal(current => ({
                          ...current,
                          calling_sms_enabled: nextIsVgus && current.calling_sms_enabled,
                          calling_whatsapp_enabled: !nextIsVgus && current.calling_whatsapp_enabled,
                        }))
                        setSearch(label(item))
                        setOpen(false)
                      }}
                    >
                      {renderLabel(item)}
                    </button>
                  ))}
                  {!options.length && (
                    <div className="text-muted p-3">No companies found.</div>
                  )}
                </div>
              )}
            </div>
            <Button
              color="primary"
              onClick={save}
              disabled={!company || loadingSettings || saving}
            >
              {saving ? "Saving..." : "Save settings"}
            </Button>
          </div>
        </div>
        <Card className="bg-form">
          <CardBody>
            <div className="alert alert-info">
              These settings apply only to screening-call outreach, automatic
              invitations, call reminders, missed-call and reschedule messages,
              and CalendNow booking confirmations. They do not change the
              separate automation settings.
            </div>
            {loadingSettings && (
              <div className="text-muted mb-3">
                Loading selected company settings...
              </div>
            )}
            <p className="small text-muted mb-3">
              {isVgus
                ? "SMS is available for VGUS. WhatsApp is unavailable."
                : "WhatsApp is available. SMS is unavailable."}
            </p>
            <Row className="g-3">
              {[
                [
                  "calling_email_enabled",
                  "Email",
                  "Send calling workflow notifications by email.",
                ],
                [
                  "calling_sms_enabled",
                  "SMS",
                  "Send calling workflow notifications by SMS.",
                ],
                [
                  "calling_whatsapp_enabled",
                  "WhatsApp",
                  "Send calling workflow notifications by WhatsApp.",
                ],
              ].filter(([field]) => supportedChannelFields.includes(field)).map(([field, title, help]) => (
                <Col lg={4} key={field}>
                  <div className="border rounded p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="fw-semibold mb-0" htmlFor={field}>
                        {title}
                      </label>
                      <InputSwitch
                        inputId={field}
                        checked={settings[field]}
                        onChange={event => update(field, event.value)}
                        disabled={!company || loadingSettings || saving}
                      />
                    </div>
                    <small className="text-muted d-block mt-2">{help}</small>
                  </div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}
